import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, formatApiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { Loader2, MailCheck, LogOut } from "lucide-react";
import { toast } from "sonner";

function homeFor(role) {
  return role === "admin" ? "/admin" : role === "employee" ? "/employee" : "/dashboard";
}

export default function VerifyEmail() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  const verify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError("");
    try {
      await api.post("/auth/verify-email", { code: code.trim() });
      const u = await refreshUser();
      toast.success("Email verified — welcome to Sites UX!");
      navigate(homeFor(u.role));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-code");
      toast.success("A new code is on its way to your email");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-background px-6 text-white">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo className="text-3xl" /></div>
        <div className="glass-panel p-8">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
            <MailCheck className="h-7 w-7" />
          </div>
          <h1 className="mt-5 text-center font-display text-2xl font-bold">Verify your email</h1>
          <p className="mt-2 text-center text-sm text-zinc-400">
            We sent a 6-digit code to <span className="text-zinc-200">{user?.email}</span>. Enter it below to continue.
          </p>
          <form onSubmit={verify} className="mt-6 space-y-4">
            <Input
              data-testid="verify-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              placeholder="000000"
              className="rounded-sm border-white/10 bg-black text-center font-mono text-2xl tracking-[0.5em]"
            />
            {error && <p data-testid="verify-error" className="text-sm text-destructive">{error}</p>}
            <Button data-testid="verify-submit-btn" type="submit" disabled={verifying || code.length !== 6}
              className="w-full rounded-full bg-primary text-white hover:bg-primary/90 disabled:opacity-40">
              {verifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Verify email
            </Button>
          </form>
          <div className="mt-6 flex items-center justify-between text-sm">
            <button data-testid="verify-resend-btn" onClick={resend} disabled={resending}
              className="text-primary hover:underline disabled:opacity-50">
              {resending ? "Sending…" : "Resend code"}
            </button>
            <button data-testid="verify-logout-btn" onClick={() => { logout(); navigate("/"); }}
              className="flex items-center gap-1 text-zinc-500 hover:text-zinc-300">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
